import { useEffect, useState } from 'react';
import { api } from '../api';

type AdminSettings = {
  id: string;
  quiet_hours_enabled: boolean;
  quiet_hours_start_hour: number;
  quiet_hours_end_hour: number;
  timezone: string;
  notification_recipients: string;
  updated_at: string;
};

export default function Settings() {
  const [data, setData] = useState<AdminSettings | null>(null);
  const [edited, setEdited] = useState<Partial<AdminSettings>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function reload() {
    setError(null);
    try {
      const r = await api.getSettings();
      setData(r);
      setEdited({});
    } catch (err: any) {
      setError(err?.message || 'Could not load settings.');
    }
  }

  useEffect(() => { reload(); }, []);

  function set(k: keyof AdminSettings, v: any) {
    setEdited((e) => ({ ...e, [k]: v }));
    setSaved(false);
  }

  function getValue<K extends keyof AdminSettings>(k: K): AdminSettings[K] | undefined {
    if (k in edited) return edited[k] as any;
    return data?.[k];
  }

  const dirty = Object.keys(edited).length > 0;

  async function handleSave() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      await api.patchSettings(edited);
      await reload();
      setSaved(true);
    } catch (err: any) {
      setError(err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  if (!data && !error) return <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>;
  if (!data) return <p className="font-serif text-accent">{error}</p>;

  return (
    <div className="max-w-2xl">
      <h1 className="font-display text-3xl font-medium text-ink mb-2">Settings</h1>
      <p className="font-serif text-sm text-ink-light mb-8">
        Last updated {new Date(data.updated_at).toLocaleString()}.
      </p>

      <Section title="Notifications">
        <Field label="Quiet hours">
          <label className="flex items-center gap-2 font-serif text-sm">
            <input
              type="checkbox"
              checked={!!getValue('quiet_hours_enabled')}
              onChange={(e) => set('quiet_hours_enabled', e.target.checked)}
              className="w-4 h-4"
            />
            Hold internal-notification email during quiet hours
          </label>
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Start hour (Eastern)">
            <input
              type="number" min="0" max="23"
              value={getValue('quiet_hours_start_hour') ?? 22}
              onChange={(e) => set('quiet_hours_start_hour', parseInt(e.target.value, 10))}
              className="w-full bg-paper border border-rule px-3 py-1.5 font-mono text-sm focus:outline-none focus:border-accent"
            />
          </Field>
          <Field label="End hour (Eastern)">
            <input
              type="number" min="0" max="23"
              value={getValue('quiet_hours_end_hour') ?? 7}
              onChange={(e) => set('quiet_hours_end_hour', parseInt(e.target.value, 10))}
              className="w-full bg-paper border border-rule px-3 py-1.5 font-mono text-sm focus:outline-none focus:border-accent"
            />
          </Field>
        </div>
        <Field label="Timezone">
          <input
            type="text"
            value={getValue('timezone') ?? 'America/New_York'}
            onChange={(e) => set('timezone', e.target.value)}
            className="w-full bg-paper border border-rule px-3 py-1.5 font-mono text-sm focus:outline-none focus:border-accent"
          />
        </Field>
        <Field label="Notification recipients (comma-separated)">
          <input
            type="text"
            value={getValue('notification_recipients') ?? ''}
            onChange={(e) => set('notification_recipients', e.target.value)}
            className="w-full bg-paper border border-rule px-3 py-1.5 font-mono text-sm focus:outline-none focus:border-accent"
          />
        </Field>
      </Section>

      <div className="mt-8 pt-6 border-t border-rule flex items-center justify-between">
        <div className="text-xs font-mono uppercase tracking-widest">
          {saving ? <span className="text-ink-light">Saving…</span> :
           saved && !dirty ? <span className="text-emerald-700">Saved.</span> :
           dirty ? <span className="text-accent">Unsaved changes</span> : <span className="text-ink-light">Up to date</span>}
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-5 py-2 bg-ink text-paper font-serif text-sm hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Save settings
        </button>
      </div>

      {error && <p className="mt-4 text-accent font-serif text-sm">{error}</p>}

      <div className="mt-12 p-4 bg-cream-dark border-l-2 border-gold font-serif text-sm text-ink-soft">
        <strong className="text-ink">Note:</strong> The Worker currently reads quiet-hours from hardcoded constants
        (22:00–07:00 ET). Wiring it to read from this table is a small follow-on. For now, edits here are
        persisted but only consumed by future code paths — see <code className="font-mono text-xs">cadence-advisers/operational/email-templates/tempo-v2-README.md</code>.
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-8 pb-6 border-b border-rule last:border-b-0">
      <h2 className="font-display text-xl font-medium text-ink mb-4">{title}</h2>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] uppercase tracking-wider text-ink-light mb-1 block">{label}</span>
      {children}
    </label>
  );
}
